import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  ConfigProvider,
  Layout,
  Typography,
  Input,
  Button,
  Card,
  Row,
  Col,
  Empty,
  Space,
  message,
  Spin,
  Popconfirm,
  Tag,
  Modal,
  Form,
  Select,
  Switch,
  Checkbox,
  Divider,
  Tabs,
  Badge,
  InputNumber,
  Dropdown,
  Alert,
  Avatar,
  AutoComplete,
  Grid,
  theme,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  UserOutlined,
  ReloadOutlined,
  TrophyOutlined,
  CrownOutlined,
  EditOutlined,
  UsergroupAddOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  HistoryOutlined,
  SaveOutlined,
  DownOutlined,
  LogoutOutlined,
  BankOutlined,
  ThunderboltOutlined,
  ExportOutlined,
  FileTextOutlined,
  BarChartOutlined,
  MoonOutlined,
  SunOutlined,
  DesktopOutlined,
} from '@ant-design/icons';
import LoginPage, { UserProfileModal } from './LoginPage';
import InitPage from './InitPage';
import BonusBank from './BonusBank';
import BankCounter from './BankCounter';
import BossRush from './BossRush';
import Rules from './Rules';
import DropStats from './DropStats';
import { AdminPage } from './BossConfig';
import { CreateRunModal, EditRunNameModal, BossTypeTag, getBossStatusOptions, loadBossConfig } from './RunManager';
import { ROLE_OPTIONS } from './constants';

const { Header, Content, Footer } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const { Option } = Select;

// 后端代理服务器地址
const API_BASE_URL = '';

// 队伍配置
const MAX_TEAM_MEMBERS = 6;
const TEAM_COLORS = {
  1: '#ff6b6b', 2: '#4ecdc4', 3: '#45b7d1', 4: '#96ceb4', 5: '#ffeaa7',
  6: '#dfe6e9', 7: '#a29bfe', 8: '#fd79a8', 9: '#00b894', 10: '#e17055',
};

// 角色类型选项 - 从 constants.js 导入（ROLE_OPTIONS 已在 import 语句中导入）
// 特殊需求选项
const SPECIAL_NEEDS_OPTIONS = [
  { value: 'NeedSI', label: 'Need SI' },
  { value: 'NeedSE', label: 'Need SE' },
  { value: 'NeedHB', label: 'Need HB' },
];

// Round 完成度状态选项
const ROUND_STATUS_OPTIONS = [
  { value: 'NotDone', label: 'NotDone', color: 'default' },
  { value: 'Pre-hd(L)', label: 'Pre-hd(L)', color: 'purple' },
  { value: 'Pre-hd(R)', label: 'Pre-hd(R)', color: 'purple' },
  { value: 'Legs', label: 'Legs', color: 'blue' },
  { value: 'Tail', label: 'Tail', color: 'cyan' },
  { value: 'Wings', label: 'Wings', color: 'geekblue' },
  { value: 'Right-hd', label: 'Right-hd', color: 'orange' },
  { value: 'Left-hd', label: 'Left-hd', color: 'orange' },
  { value: 'Mid-hd', label: 'Mid-hd', color: 'volcano' },
  { value: 'AllDone', label: 'AllDone', color: 'green' },
  { value: 'Abandoned', label: 'Abandoned', color: 'red' },
];

// 职业颜色映射
const getJobColor = (job) => {
  if (!job) return '#d9d9d9';
  const j = job.toLowerCase();
  if (j.includes('warrior') || j.includes('hero') || j.includes('paladin') || j.includes('dark knight')) return '#fa8c16';
  if (j.includes('magician') || j.includes('mage') || j.includes('arch') || j.includes('bishop')) return '#1890ff';
  if (j.includes('bowman') || j.includes('ranger') || j.includes('sniper') || j.includes('marksman')) return '#52c41a';
  if (j.includes('thief') || j.includes('assassin') || j.includes('bandit') || j.includes('shadower') || j.includes('hermit') || j.includes('night lord')) return '#faad14';
  if (j.includes('pirate') || j.includes('brawler') || j.includes('gunslinger') || j.includes('buccaneer') || j.includes('corsair')) return '#722ed1';
  return '#d9d9d9';
};

const getJobClassLabel = (job) => {
  if (!job) return '';
  const j = job.toLowerCase();
  if (j.includes('warrior') || j.includes('hero') || j.includes('paladin') || j.includes('dark knight')) return '战士';
  if (j.includes('magician') || j.includes('mage') || j.includes('arch') || j.includes('bishop')) return '法师';
  if (j.includes('bowman') || j.includes('ranger') || j.includes('sniper') || j.includes('marksman')) return '弓手';
  if (j.includes('thief') || j.includes('assassin') || j.includes('bandit') || j.includes('shadower') || j.includes('hermit') || j.includes('night lord')) return '飞侠';
  if (j.includes('pirate') || j.includes('brawler') || j.includes('gunslinger') || j.includes('buccaneer') || j.includes('corsair')) return '海盗';
  return '';
};

const CLASS_COLORS = {
  '战士': '#fa8c16',
  '法师': '#1890ff',
  '弓手': '#52c41a',
  '飞侠': '#faad14',
  '海盗': '#722ed1',
};
const CharacterCard = ({ character, onDelete, onEdit, onStatusChange, isHistoryView, statusOptions, currentUser, isHost, isAdmin, linkedCharacters, characters, onJobLoaded }) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  // 判断当前用户是否可以编辑此角色
  // Host 或管理员可以编辑所有人的角色，普通用户只能编辑自己的角色
  const canEditCharacter = useMemo(() => {
    if (!currentUser || !linkedCharacters) return false;
    // Host 或管理员可以编辑所有角色
    if (isHost || isAdmin) return true;
    // 普通用户只能编辑自己关联的角色
    const charLower = character.name.toLowerCase();
    return linkedCharacters[charLower] === currentUser.username;
  }, [currentUser, linkedCharacters, character.name, isHost, isAdmin]);

  // 判断当前用户是否可以修改状态（Host 或管理员）
  const canChangeStatus = isHost || isAdmin;

  const [imageStatus, setImageStatus] = useState('loading');
  // imageKey 初始为 0，仅手动刷新时才更新；避免每次 mount 都生成新时间戳导致浏览器缓存失效
  const [imageKey, setImageKey] = useState(0);
  const [characterInfo, setCharacterInfo] = useState(null);
  const [infoLoading, setInfoLoading] = useState(true);
  
  const imageUrl = `${API_BASE_URL}/api/getavatar?name=${encodeURIComponent(character.name)}${character.isLeader ? '&mount=1' : ''}`;
  const infoUrl = `${API_BASE_URL}/api/character?name=${encodeURIComponent(character.name)}`;

  const onJobLoadedRef = useRef(onJobLoaded);
  useEffect(() => { onJobLoadedRef.current = onJobLoaded; }, [onJobLoaded]);

  const fetchCharacterInfo = useCallback(() => {
    setInfoLoading(true);
    fetch(infoUrl, { method: 'GET', headers: { 'Accept': 'application/json' } })
      .then(response => { if (!response.ok) throw new Error('err'); return response.json(); })
      .then(data => { setCharacterInfo(data && data.name ? data : null); setInfoLoading(false); if (data?.job && onJobLoadedRef.current) onJobLoadedRef.current(character.name, data.job); })
      .catch(() => { setCharacterInfo(null); setInfoLoading(false); });
  }, [infoUrl, character.name]);

  useEffect(() => { fetchCharacterInfo(); }, [fetchCharacterInfo]);

  const handleRefresh = () => { setImageStatus('loading'); setImageKey(Date.now()); fetchCharacterInfo(); };

  const getRoleColor = (role) => ROLE_OPTIONS.find(r => r.value === role)?.color || 'default';
  const getRoleLabel = (role) => ROLE_OPTIONS.find(r => r.value === role)?.label || role;
  const teamColor = TEAM_COLORS[character.team] || '#dfe6e9';
  const jobColor = characterInfo?.job ? getJobColor(characterInfo.job) : '#d9d9d9';
  const jobClassLabel = characterInfo?.job ? getJobClassLabel(characterInfo.job) : '';
  const roles = Array.isArray(character.roles) ? character.roles : (character.role ? [character.role] : []);

  const r1Status = character.round1Status || 'NotDone';
  const r2Status = character.round2Status || 'NotDone';

  // 使用传入的 statusOptions 或默认选项
  const effectiveOptions = statusOptions || ROUND_STATUS_OPTIONS;
  const getStatusColorLocal = (status) => {
    const opt = effectiveOptions.find(o => o.value === status);
    return opt ? opt.color : 'default';
  };
  const statusMenuItems = effectiveOptions.map(opt => ({ key: opt.value, label: <Tag color={opt.color}>{opt.label}</Tag> }));

  return (
    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 4, height: '100%' }}>
      <Card hoverable style={{ borderRadius: 16, overflow: 'hidden', border: character.isLeader ? '2px solid #faad14' : '1px solid #e8e8e8', boxShadow: character.isLeader ? '0 4px 12px rgba(250, 173, 20, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.08)', flex: 1, display: 'flex', flexDirection: 'column' }} styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', flex: 1 } }}
        actions={isHistoryView ? [] : [
          <ReloadOutlined key="refresh" onClick={handleRefresh} style={{ color: '#52c41a' }} />,
          canEditCharacter ? (
            <EditOutlined key="edit" onClick={() => onEdit(character)} style={{ color: '#1890ff' }} />
          ) : (
            <EditOutlined key="edit" style={{ color: '#d9d9d9', cursor: 'not-allowed' }} title="只能编辑自己的角色" />
          ),
          canEditCharacter ? (
            character.roles?.includes('host') && characters?.length > 1 ? (
              <DeleteOutlined key="delete" style={{ color: '#d9d9d9', cursor: 'not-allowed' }} title="Host 不能直接删除，请先转移 Host 身份" />
            ) : (
              <Popconfirm key="delete" title="确定删除？" onConfirm={() => onDelete(character.id)} okText="确定" cancelText="取消"><DeleteOutlined style={{ color: '#ff4d4f' }} /></Popconfirm>
            )
          ) : (
            <DeleteOutlined key="delete" style={{ color: '#d9d9d9', cursor: 'not-allowed' }} title="只能删除自己的角色" />
          ),
        ]}>
        {/* 队伍和队长标签 */}
        <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 10, background: teamColor, borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 'bold', color: '#333' }}>队伍 {character.team}</div>
        {character.isLeader && <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 10, background: '#faad14', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CrownOutlined style={{ color: '#fff', fontSize: 18 }} /></div>}

        <div style={{ height: isMobile ? 70 : 120, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `linear-gradient(135deg, ${jobColor}30 0%, ${teamColor}20 100%)`, position: 'relative' }}>
          {imageStatus === 'loading' && <Spin size={isMobile ? 'small' : 'large'} style={{ position: 'absolute' }} />}
          {imageStatus === 'error' && <UserOutlined style={{ fontSize: isMobile ? 28 : 40, opacity: 0.5, color: '#666' }} />}
          <img key={imageKey} src={imageKey ? `${imageUrl}&_t=${imageKey}` : imageUrl} alt={character.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: imageStatus === 'loaded' ? 'block' : 'none' }} onLoad={() => setImageStatus('loaded')} onError={() => setImageStatus('error')} />
        </div>

        <div style={{ padding: isMobile ? '8px 8px' : '10px 10px', background: 'var(--surface-solid)', flex: 1, display: 'flex', flexDirection: 'column' }}>
          {infoLoading ? <div style={{ textAlign: 'center', padding: '10px 0' }}><Spin size="small" /></div> : (
            <>
              <div style={{ textAlign: 'center', marginBottom: 2 }}><Text strong style={{ fontSize: 14, color: '#333', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={characterInfo?.name || character.name}>{characterInfo?.name || character.name}</Text></div>
              {/* 移动端只显示 职业 + 等级，隐藏公会/特殊需求/道具标签 */}
              {!isMobile && <div style={{ textAlign: 'center', marginBottom: 6 }}>{characterInfo?.guild ? <Text type="secondary" style={{ fontSize: 11 }}>〈{characterInfo.guild}〉</Text> : <Text type="secondary" style={{ fontSize: 11 }}>无公会</Text>}</div>}
              {characterInfo && <div style={{ display: 'flex', justifyContent: 'center', gap: 2, marginBottom: isMobile ? 0 : 6, marginTop: isMobile ? 4 : 0, flexWrap: 'wrap' }}><Tag icon={<TrophyOutlined />} color="gold" style={{ margin: 1, fontSize: 10 }}>Lv.{characterInfo.level || '?'}</Tag><Tag color={jobColor} style={{ margin: 1, fontSize: 10 }}>{jobClassLabel} {characterInfo.job || '未知'}</Tag></div>}
              {!isMobile && character.specialNeeds?.length > 0 && <div style={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', marginBottom: 4 }}>{character.specialNeeds.map(need => <Tag key={need} color="volcano" style={{ margin: 1, fontSize: 10 }}>{need}</Tag>)}</div>}
              {!isMobile && (character.needPendant || character.needChair || character.needCard) && (
                <><Divider style={{ margin: '6px 0', borderColor: '#f0f0f0' }} dashed /><div style={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>{character.needPendant && <Tag color="pink" style={{ margin: 1, fontSize: 10 }}>Pendant</Tag>}{character.needChair && <Tag color="lime" style={{ margin: 1, fontSize: 10 }}>Chair</Tag>}{character.needCard && <Tag color="geekblue" style={{ margin: 1, fontSize: 10 }}>Card</Tag>}</div></>
              )}
              {/* 移动端：角色 Tag 直接放进卡片内，推到底部对齐 */}
              {isMobile && roles.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', marginTop: 'auto', paddingTop: 6 }}>
                  {roles.map((role, i) => <Tag key={i} color={getRoleColor(role)} style={{ margin: 0, fontSize: 10, padding: '1px 6px', borderRadius: 4 }}>{getRoleLabel(role)}</Tag>)}
                </div>
              )}
            </>
          )}
        </div>

        {/* R1/R2 状态区域 - 卡片底部（移动端不显示，仅用于登记名单）*/}
        {!isMobile && (character.round1 || character.round2) && (
          <div style={{ background: 'var(--surface-subtle)', padding: '8px 10px' }}>
            {character.round1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: character.round2 ? 6 : 0 }}>
                <Text style={{ fontSize: 12, fontWeight: 'bold' }}>R1:</Text>
                {isHistoryView || !canChangeStatus ? (
                  <Tag color={getStatusColorLocal(r1Status)} style={{ margin: 0 }} title={!canChangeStatus && !isHistoryView ? '只有 Host 或管理员可以修改状态' : ''}>{r1Status}</Tag>
                ) : (
                  <Dropdown menu={{ items: statusMenuItems, onClick: ({key}) => onStatusChange(character.id, 'round1Status', key) }} trigger={['click']}>
                    <Tag color={getStatusColorLocal(r1Status)} style={{ margin: 0, cursor: 'pointer' }}>{r1Status} <DownOutlined style={{ fontSize: 10 }} /></Tag>
                  </Dropdown>
                )}
              </div>
            )}
            {character.round2 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Text style={{ fontSize: 12, fontWeight: 'bold' }}>R2:</Text>
                {isHistoryView || !canChangeStatus ? (
                  <Tag color={getStatusColorLocal(r2Status)} style={{ margin: 0 }} title={!canChangeStatus && !isHistoryView ? '只有 Host 或管理员可以修改状态' : ''}>{r2Status}</Tag>
                ) : (
                  <Dropdown menu={{ items: statusMenuItems, onClick: ({key}) => onStatusChange(character.id, 'round2Status', key) }} trigger={['click']}>
                    <Tag color={getStatusColorLocal(r2Status)} style={{ margin: 0, cursor: 'pointer' }}>{r2Status} <DownOutlined style={{ fontSize: 10 }} /></Tag>
                  </Dropdown>
                )}
              </div>
            )}
          </div>
        )}
      </Card>
      {/* 桌面端：角色 Tag 竖排在卡片右侧；移动端已放入卡片内部 */}
      {!isMobile && roles.length > 0 && <div style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingTop: 30 }}>{roles.map((role, i) => <Tag key={i} color={getRoleColor(role)} style={{ margin: 0, fontSize: 10, padding: '2px 4px', lineHeight: '14px', writingMode: 'vertical-rl', textOrientation: 'mixed', borderRadius: 4 }}>{getRoleLabel(role)}</Tag>)}</div>}
    </div>
  );
};

// Add/Edit Character Modal (简化版，保持原有功能)
const CharacterModal = ({ visible, onCancel, onSave, editingCharacter, characters, teamCount, bossType, isCurrentUserHost, currentUser, linkedCharacters }) => {
  const [form] = Form.useForm();
  const teamInfo = useMemo(() => {
    const info = {};
    for (let i = 1; i <= teamCount; i++) {
      const members = characters.filter(c => c.team === i && c.id !== editingCharacter?.id);
      info[i] = { count: members.length, hasLeader: members.some(c => c.isLeader) };
    }
    return info;
  }, [characters, editingCharacter, teamCount]);

  // 检查当前 Run 是否已有 Host
  const currentHost = useMemo(() => {
    return characters.find(c => c.roles?.includes('host') && c.id !== editingCharacter?.id);
  }, [characters, editingCharacter]);

  // 当前编辑的角色是否是 Host
  const isEditingHost = editingCharacter?.roles?.includes('host');

  const selectedTeam = Form.useWatch('team', form);
  const currentTeamHasLeader = selectedTeam ? teamInfo[selectedTeam]?.hasLeader : false;

  // 仅在弹窗打开（或切换编辑对象）的那一刻初始化表单一次。
  // 注意：不要依赖 teamInfo / characters，否则后台 30 秒轮询刷新 characters 时
  // 会重跑本 effect 并 resetFields，把用户正在填写的内容清空（"刷没了"）。
  const teamInfoRef = useRef(teamInfo);
  teamInfoRef.current = teamInfo;
  useEffect(() => {
    if (visible && editingCharacter) {
      const editData = { ...editingCharacter };
      if (!editData.roles && editData.role) editData.roles = [editData.role];
      form.setFieldsValue(editData);
    } else if (visible) {
      form.resetFields();
      const info = teamInfoRef.current;
      let defaultTeam = 1;
      for (let i = 1; i <= teamCount; i++) { if (info[i] && info[i].count < MAX_TEAM_MEMBERS) { defaultTeam = i; break; } }
      // PB 只能 Round 1，NT 不需要选择 Round，其他 Boss 默认勾选所有 Round
      const isPB = bossType === 'PB';
      form.setFieldsValue({ isLeader: false, roles: ['attacker'], team: defaultTeam, round1: true, round2: !isPB, round1Status: 'NotDone', round2Status: 'NotDone', needPendant: false, needChair: false, needCard: false, specialNeeds: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, editingCharacter, form, teamCount, bossType]);

  // 处理角色选择变化，确保 Host 唯一性
  const handleRolesChange = (newRoles) => {
    // 如果尝试添加 host 角色
    if (newRoles.includes('host') && !isEditingHost) {
      // 如果已经有其他 Host，不允许添加
      if (currentHost) {
        message.warning(`当前 Run 已有 Host: ${currentHost.name}，每个 Run 只能有一个 Host`);
        // 移除 host 角色
        form.setFieldsValue({ roles: newRoles.filter(r => r !== 'host') });
        return;
      }
    }
  };

  const handleOk = () => {
    form.validateFields().then(values => {
      if (teamInfo[values.team]?.count >= MAX_TEAM_MEMBERS) { message.error('队伍已满！'); return; }
      if (values.isLeader && teamInfo[values.team]?.hasLeader && !editingCharacter?.isLeader) { message.error('该队已有队长！'); return; }
      
      // 检查 Host 唯一性
      if (values.roles?.includes('host') && !isEditingHost && currentHost) {
        message.error(`当前 Run 已有 Host: ${currentHost.name}，每个 Run 只能有一个 Host`);
        return;
      }
      
      // 如果当前是 Host 想要移除自己的 Host 角色
      if (isEditingHost && !values.roles?.includes('host')) {
        message.error('Host 不能移除自己的 Host 身份，请使用"转移 Host"功能');
        return;
      }
      
      onSave(values);
      form.resetFields();
    });
  };

  // 过滤角色选项：如果已有 Host 且不是当前编辑的角色，禁用 host 选项
  const getFilteredRoleOptions = () => {
    return ROLE_OPTIONS.map(o => ({
      ...o,
      disabled: o.value === 'host' && currentHost && !isEditingHost
    }));
  };

  // 获取当前用户已绑定的角色（排除已在当前 Run 中的角色）
  const availableUserCharacters = useMemo(() => {
    if (!currentUser?.characters) return [];
    const existingNames = characters.map(c => c.name.toLowerCase());
    return currentUser.characters.filter(char => !existingNames.includes(char.toLowerCase()));
  }, [currentUser?.characters, characters]);

  return (
    <Modal title={editingCharacter ? '编辑角色' : '添加角色'} open={visible} onCancel={onCancel} onOk={handleOk} okText="保存" cancelText="取消" width={500}>
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="角色名" rules={[{ required: true, message: '请输入或选择角色名' }]}>
          {editingCharacter ? (
            <Input prefix={<UserOutlined />} disabled />
          ) : (
            <AutoComplete
              placeholder="输入角色名或选择已绑定角色"
              options={availableUserCharacters.map(char => ({
                value: char,
                label: (
                  <Space>
                    <img 
                      src={`${API_BASE_URL}/api/getavatar?name=${encodeURIComponent(char)}`}
                      alt=""
                      style={{ width: 24, height: 24, borderRadius: 4 }}
                      onError={e => e.target.style.display = 'none'}
                    />
                    {char}
                    <Tag color="green" style={{ fontSize: 10 }}>已绑定</Tag>
                  </Space>
                )
              }))}
              filterOption={(inputValue, option) =>
                option.value.toLowerCase().includes(inputValue.toLowerCase())
              }
              style={{ width: '100%' }}
            >
              <Input prefix={<UserOutlined />} placeholder="输入角色名或选择已绑定角色" maxLength={12} />
            </AutoComplete>
          )}
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}><Form.Item name="team" label="选择队伍" rules={[{ required: true }]}><Select placeholder="选择队伍">{Array.from({ length: teamCount }, (_, i) => i + 1).map(n => <Option key={n} value={n} disabled={teamInfo[n]?.count >= MAX_TEAM_MEMBERS}><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: TEAM_COLORS[n], marginRight: 8 }} />队伍 {n} ({teamInfo[n]?.count || 0}/{MAX_TEAM_MEMBERS}){teamInfo[n]?.hasLeader && <CrownOutlined style={{ marginLeft: 4, color: '#faad14' }} />}</Option>)}</Select></Form.Item></Col>
          <Col span={12}><Form.Item name="isLeader" label="队长" valuePropName="checked"><Switch checkedChildren={<CrownOutlined />} unCheckedChildren="否" disabled={currentTeamHasLeader && !editingCharacter?.isLeader} /></Form.Item></Col>
        </Row>
        {currentHost && !isEditingHost && (
          <Alert message={`当前 Host: ${currentHost.name}`} type="info" showIcon style={{ marginBottom: 16 }} />
        )}
        <Form.Item name="roles" label="角色定位（可多选）" rules={[{ required: true }]}>
          <Select mode="multiple" placeholder="选择角色定位" maxTagCount={3} onChange={handleRolesChange}>
            {getFilteredRoleOptions().map(o => (
              <Option key={o.value} value={o.value} disabled={o.disabled}>
                <Tag color={o.color}>{o.label}</Tag>
                {o.disabled && <Text type="secondary" style={{ fontSize: 10 }}> (已有Host)</Text>}
              </Option>
            ))}
          </Select>
        </Form.Item>
        {bossType !== 'NT' && (
          <>
            <Divider>回合选择 {bossType === 'PB' && <Text type="secondary" style={{ fontSize: 12 }}>(PB 只有 1 Round)</Text>}</Divider>
            <Row gutter={16}>
              <Col span={12}><Form.Item name="round1" valuePropName="checked"><Checkbox>Round 1</Checkbox></Form.Item></Col>
              <Col span={12}><Form.Item name="round2" valuePropName="checked"><Checkbox disabled={bossType === 'PB'}>Round 2</Checkbox></Form.Item></Col>
            </Row>
          </>
        )}
        <Divider>物品需求</Divider>
        <Row gutter={16}><Col span={8}><Form.Item name="needPendant" valuePropName="checked"><Checkbox>Pendant</Checkbox></Form.Item></Col><Col span={8}><Form.Item name="needChair" valuePropName="checked"><Checkbox>Chair</Checkbox></Form.Item></Col><Col span={8}><Form.Item name="needCard" valuePropName="checked"><Checkbox>Card</Checkbox></Form.Item></Col></Row>
        <Divider>特殊需求</Divider>
        <Form.Item name="specialNeeds"><Checkbox.Group options={SPECIAL_NEEDS_OPTIONS} /></Form.Item>
      </Form>
    </Modal>
  );
};

// Batch Update Modal - 每个角色可以选择不同状态
const BatchUpdateModal = ({ visible, onCancel, onConfirm, round, characters, bossType, bossConfig }) => {
  const [statusMap, setStatusMap] = useState({});
  const [quickSelect, setQuickSelect] = useState(null);

  // 根据 Boss 类型和配置获取状态选项
  const statusOptions = useMemo(() => {
    return getBossStatusOptions(bossType, bossConfig);
  }, [bossType, bossConfig]);

  // 计算受影响的角色
  const affectedChars = useMemo(() => {
    return characters.filter(c => round === 1 ? c.round1 : c.round2);
  }, [characters, round]);

  // 初始化状态映射 - 仅在弹窗打开时执行
  useEffect(() => {
    if (visible) {
      const field = round === 1 ? 'round1Status' : 'round2Status';
      const initialMap = {};
      characters.filter(c => round === 1 ? c.round1 : c.round2).forEach(c => {
        initialMap[c.id] = c[field] || 'NotDone';
      });
      setStatusMap(initialMap);
      setQuickSelect(null); // 重置快速选择
    }
  }, [visible, round, characters]);

  const handleStatusChange = (charId, status) => {
    setStatusMap(prev => ({ ...prev, [charId]: status }));
  };

  const handleSetAllStatus = (status) => {
    const newMap = {};
    affectedChars.forEach(c => { newMap[c.id] = status; });
    setStatusMap(newMap);
    setQuickSelect(status);
  };

  const handleConfirm = () => {
    onConfirm(round, statusMap);
  };

  return (
    <Modal title={`批量更新 Round ${round} 状态`} open={visible} onCancel={onCancel} onOk={handleConfirm} okText="确认更新" cancelText="取消" width={600}>
      <div style={{ marginBottom: 16 }}>
        <Text>快速设置全部为：</Text>
        <Select 
          placeholder="选择状态" 
          style={{ width: 150, marginLeft: 8 }} 
          value={quickSelect}
          onChange={handleSetAllStatus}
          allowClear
          onClear={() => setQuickSelect(null)}
        >
          {statusOptions.map(o => <Option key={o.value} value={o.value}><Tag color={o.color}>{o.label}</Tag></Option>)}
        </Select>
      </div>
      <Divider style={{ margin: '12px 0' }} />
      <div style={{ maxHeight: 400, overflow: 'auto' }}>
        {affectedChars.map(c => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: TEAM_COLORS[c.team] }} />
              <Text strong>{c.name}</Text>
              {c.isLeader && <CrownOutlined style={{ color: '#faad14' }} />}
            </div>
            <Select value={statusMap[c.id] || 'NotDone'} onChange={(v) => handleStatusChange(c.id, v)} style={{ width: 140 }}>
              {statusOptions.map(o => <Option key={o.value} value={o.value}><Tag color={o.color}>{o.label}</Tag></Option>)}
            </Select>
          </div>
        ))}
      </div>
      {affectedChars.length === 0 && <Empty description={`没有参与 Round ${round} 的角色`} />}
    </Modal>
  );
};

// Boss Type Options
const BOSS_TYPE_OPTIONS = [
  { value: 'NT', label: 'NT (Neo Tokyo Boss Rush)', color: '#52c41a' },
  { value: 'HT', label: 'HT (Horntail)', color: '#fa8c16' },
  { value: 'PB', label: 'PB (Pink Bean)', color: '#eb2f96' },
];

// Complete Run Modal - 完成运行弹窗
const CompleteRunModal = ({ visible, onCancel, onConfirm, bossType }) => {
  const [customName, setCustomName] = useState('');

  // 生成默认名称
  const generateDefaultName = useCallback((type) => {
    const now = new Date();
    const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    return `${dateStr} ${timeStr}-${type}`;
  }, []);

  // 当弹窗打开时生成默认名称
  useEffect(() => {
    if (visible) {
      setCustomName(generateDefaultName(bossType));
    }
  }, [visible, bossType, generateDefaultName]);

  const handleConfirm = () => {
    onConfirm(customName);
  };

  // 获取当前 Boss 类型的显示信息
  const currentBossOption = BOSS_TYPE_OPTIONS.find(opt => opt.value === bossType) || BOSS_TYPE_OPTIONS[0];

  return (
    <Modal title="完成运行" open={visible} onCancel={onCancel} onOk={handleConfirm} okText="确认完成" cancelText="取消" okButtonProps={{ danger: true }}>
      <p style={{ color: '#666' }}>将当前运行保存到历史记录并清空数据。</p>
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Boss 类型：</Text>
        <Tag color={currentBossOption.color} style={{ padding: '4px 12px', fontSize: 14 }}>
          {currentBossOption.label}
        </Tag>
      </div>
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>运行名称：</Text>
        <Input 
          value={customName} 
          onChange={e => setCustomName(e.target.value)} 
          placeholder="输入运行名称"
          style={{ fontSize: 14 }}
        />
      </div>
    </Modal>
  );
};

// Team Section
const TeamSection = ({ teamNum, characters: teamChars, onDelete, onEdit, onStatusChange, isHistoryView, statusOptions, currentUser, isHost, isAdmin, linkedCharacters, allCharacters, onJobLoaded }) => {
  const teamColor = TEAM_COLORS[teamNum] || '#dfe6e9';
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, padding: '8px 16px', background: `linear-gradient(90deg, ${teamColor}40 0%, transparent 100%)`, borderLeft: `4px solid ${teamColor}`, borderRadius: '0 8px 8px 0' }}>
        <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', background: teamColor, marginRight: 10 }} />
        <Text strong style={{ fontSize: 16 }}>队伍 {teamNum}</Text>
        {teamChars.some(c => c.isLeader) && <CrownOutlined style={{ marginLeft: 8, color: '#faad14' }} />}
        <Badge count={teamChars.length} style={{ marginLeft: 12, backgroundColor: teamChars.length >= MAX_TEAM_MEMBERS ? '#ff4d4f' : '#52c41a' }} />
      </div>
      {teamChars.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无成员" style={{ padding: '20px 0' }} /> : (
        <Row gutter={[12, 12]}>{teamChars.map(c => <Col key={c.id} xs={12} sm={8} md={6} lg={5} xl={4}><CharacterCard character={c} onDelete={onDelete} onEdit={onEdit} onStatusChange={onStatusChange} isHistoryView={isHistoryView} statusOptions={statusOptions} currentUser={currentUser} isHost={isHost} isAdmin={isAdmin} linkedCharacters={linkedCharacters} characters={allCharacters} onJobLoaded={onJobLoaded} /></Col>)}</Row>
      )}
    </div>
  );
};

// Main App
function App() {
  // 响应式断点：md 以下视为移动端（手机/竖屏平板）
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // 用户登录状态
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfileVisible, setUserProfileVisible] = useState(false);
  
  // 多 Run 支持
  const [runs, setRuns] = useState([]);
  const [currentRunId, setCurrentRunId] = useState(null);
  const [maxRuns, setMaxRuns] = useState(5);
  const [createRunVisible, setCreateRunVisible] = useState(false);
  const [editRunNameVisible, setEditRunNameVisible] = useState(false);
  const [transferHostVisible, setTransferHostVisible] = useState(false);
  const [runVersion, setRunVersion] = useState(1);  // 当前 Run 的版本号
  
  const [characters, setCharacters] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [teamCount, setTeamCount] = useState(2);
  const [bossType, setBossType] = useState('HT');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [batchModal, setBatchModal] = useState({ visible: false, round: 1 });
  const [historyList, setHistoryList] = useState([]);
  const [isHistoryView, setIsHistoryView] = useState(false);
  const [historyViewName, setHistoryViewName] = useState('');
  const [completeModalVisible, setCompleteModalVisible] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [mainTab, setMainTab] = useState('bossRun');
  // 主题模式：light（默认）→ dark → system 三态循环，localStorage 持久化
  const [themeMode, setThemeMode] = useState(() => {
    const saved = localStorage.getItem('themeMode');
    if (saved) return saved;
    const oldDark = localStorage.getItem('darkMode');
    return oldDark === '1' ? 'dark' : 'light';
  });
  // 系统是否偏好暗色
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const darkMode = themeMode === 'dark' || (themeMode === 'system' && systemDark);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('themeMode', themeMode);
  }, [darkMode, themeMode]);
  // 系统初始化状态（null=加载中, true=已初始化, false=未初始化）
  const [initStatus, setInitStatus] = useState(null);
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/init/status`)
      .then(r => r.json())
      .then(d => setInitStatus(!!d.initialized))
      .catch(() => setInitStatus(true)); // 查询失败时按已初始化处理，避免误拦截
  }, []);

  // 移动端手势：左右滑动切 tab、顶部下拉刷新（重新挂载当前页触发重新拉取）
  const touchStartRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY, atTop: window.scrollY <= 0 };
  };
  const handleTouchEnd = (e) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!isMobile || !start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    // 顶部下拉刷新
    if (start.atTop && dy > 80 && Math.abs(dy) > Math.abs(dx)) {
      setRefreshKey(k => k + 1);
      return;
    }
    // 左右滑动切 tab
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy)) return;
    const tabKeys = isAdmin
      ? ['rules', 'bossRun', 'bankCounter', 'dropStats', 'admin']
      : ['rules', 'bossRun', 'bankCounter', 'dropStats'];
    const idx = tabKeys.indexOf(mainTab);
    if (idx < 0) return;
    setMainTab(dx < 0 ? tabKeys[Math.min(idx + 1, tabKeys.length - 1)] : tabKeys[Math.max(idx - 1, 0)]);
  };
  // 移动端只暴露已适配的轻量页，若当前 Tab 被隐藏则回退到 bossRun
  useEffect(() => {
    const mobileTabs = ['rules', 'bossRun', 'bankCounter', 'dropStats', 'admin'];
    if (isMobile && !mobileTabs.includes(mainTab)) {
      setMainTab('bossRun');
    }
  }, [isMobile, mainTab]);
  const [bossConfig, setBossConfig] = useState(null);
  const [linkedCharacters, setLinkedCharacters] = useState({});  // 角色到用户的映射
  const [isAdmin, setIsAdmin] = useState(false);  // 当前用户是否是管理员

  // 版本号（页脚显示）
  const [version, setVersion] = useState('');
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/version`)
      .then(r => r.json())
      .then(d => setVersion(d.version || ''))
      .catch(() => {});
  }, []);

  // 角色职业缓存，用于统计各职业人数
  const [characterJobs, setCharacterJobs] = useState({});

  // 当前选中的 Run
  const currentRun = useMemo(() => {
    return runs.find(r => r.id === currentRunId) || null;
  }, [runs, currentRunId]);

  // 判断当前用户是否是当前 Run 的 Host
  const isCurrentUserHost = useMemo(() => {
    if (!currentUser || !characters.length) return false;
    // 找出所有 host 角色
    const hostCharacters = characters.filter(c => c.roles?.includes('host'));
    if (hostCharacters.length === 0) return false;
    // 检查当前用户的角色是否包含任何 host 角色
    const userChars = currentUser.characters || [];
    return hostCharacters.some(hc => 
      userChars.some(uc => uc.toLowerCase() === hc.name.toLowerCase())
    );
  }, [currentUser, characters]);

  // 检查本地存储的用户登录状态
  useEffect(() => {
    const savedUser = localStorage.getItem('maplelegends-user');
    if (savedUser) {
      try {
        setCurrentUser(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem('maplelegends-user');
      }
    }
  }, []);

  const handleLogin = (user, isNewUser = false) => {
    setCurrentUser(user);
    if (isNewUser) {
      setMainTab('rules');
    } else {
      setMainTab('bossRun');
    }
  };
  
  // 检查当前用户是否是管理员
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) {
        setIsAdmin(false);
        return;
      }
      try {
        const res = await fetch(`${API_BASE_URL}/api/users/${encodeURIComponent(currentUser.username)}/check-admin`);
        if (res.ok) {
          const data = await res.json();
          setIsAdmin(data.isAdmin || false);
        }
      } catch (e) {
        console.error(e);
      }
    };
    checkAdminStatus();
  }, [currentUser]);

  const handleLogout = () => {
    const token = localStorage.getItem('adminToken');
    setCurrentUser(null);
    localStorage.removeItem('maplelegends-user');
    localStorage.removeItem('adminToken');
    if (token) {
      fetch(`${API_BASE_URL}/api/users/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    messageApi.success('已退出登录');
  };

  // 管理员 token 失效（如系统更新后 token 被清空 / 过期）→ 跳回登录
  useEffect(() => {
    const onExpired = () => {
      setCurrentUser(null);
      localStorage.removeItem('maplelegends-user');
      messageApi.warning('登录已过期，请重新登录');
    };
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, [messageApi]);

  const loadFromServer = useCallback(async () => {
    setLoading(true);
    try {
      // 同时加载 runs、boss 配置和用户关联数据
      const [runsRes, configData, usersRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/runs`),
        loadBossConfig(),
        fetch(`${API_BASE_URL}/api/users`)
      ]);
      
      if (configData) {
        setBossConfig(configData);
      }

      // 加载角色关联信息
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setLinkedCharacters(usersData.linkedCharacters || {});
      }
      
      if (runsRes.ok) { 
        const data = await runsRes.json();
        // 过滤掉 NT 类型的 Run（NT 在 Boss Rush 标签中管理）
        const loadedRuns = (data.runs || []).filter(r => r.bossType !== 'NT');
        setRuns(loadedRuns);
        setMaxRuns(data.maxRuns || 5);
        
        // 如果有 runs，选择第一个；否则清空
        if (loadedRuns.length > 0) {
          const firstRun = loadedRuns[0];
          setCurrentRunId(firstRun.id);
          setCharacters(firstRun.characters || []);
          setTeamCount(firstRun.teamCount || 2);
          setBossType(firstRun.bossType || 'HT');
          setRunVersion(firstRun.version || 1);  // 设置版本号
        } else {
          setCurrentRunId(null);
          setCharacters([]);
          setTeamCount(2);
          setBossType('HT');
          setRunVersion(1);
        }
        setIsHistoryView(false); 
        setHistoryViewName(''); 
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  // 切换当前 Run
  const switchRun = useCallback((runId) => {
    const run = runs.find(r => r.id === runId);
    if (run) {
      setCurrentRunId(runId);
      setCharacters(run.characters || []);
      setTeamCount(run.teamCount || 2);
      setBossType(run.bossType || 'HT');
      setRunVersion(run.version || 1);  // 设置版本号
      setIsHistoryView(false);
    }
  }, [runs]);

  // 刷新当前 Run 的数据（用于定时刷新和手动刷新）
  const refreshCurrentRun = useCallback(async (silent = false) => {
    if (!currentRunId || isHistoryView) return;
    
    if (!silent) setSyncing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/runs/${currentRunId}`);
      if (res.ok) {
        const run = await res.json();
        setCharacters(run.characters || []);
        setTeamCount(run.teamCount || 2);
        setRunVersion(run.version || 1);
        // 更新本地 runs 状态
        setRuns(prev => prev.map(r => r.id === currentRunId ? { ...r, ...run } : r));
      }
    } catch (e) { console.error(e); }
    if (!silent) setSyncing(false);
  }, [currentRunId, isHistoryView]);

  // 添加角色到 Run（使用增量 API）
  const addCharacterToRun = useCallback(async (characterData) => {
    if (!currentRunId || !currentUser) return { success: false, error: '未选择 Run' };
    
    setSyncing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/runs/${currentRunId}/characters?username=${encodeURIComponent(currentUser.username)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ character: characterData, version: runVersion })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        // 更新本地状态
        setCharacters(prev => [...prev, data.character]);
        setRunVersion(data.version);
        setRuns(prev => prev.map(r => r.id === currentRunId ? { ...r, characters: [...(r.characters || []), data.character], version: data.version } : r));
        setSyncing(false);
        return { success: true, character: data.character };
      } else {
        setSyncing(false);
        // 处理特殊错误
        if (data.detail?.error === 'VERSION_CONFLICT') {
          // 版本冲突，需要刷新
          await refreshCurrentRun();
          return { success: false, error: data.detail.message, needRefresh: true };
        } else if (data.detail?.error === 'TEAM_FULL') {
          return { success: false, error: data.detail.message, teamFull: true, team: data.detail.team };
        }
        return { success: false, error: data.detail?.message || data.detail || '添加失败' };
      }
    } catch (e) {
      console.error(e);
      setSyncing(false);
      return { success: false, error: '网络错误' };
    }
  }, [currentRunId, currentUser, runVersion, refreshCurrentRun]);

  // 从 Run 中移除角色（使用增量 API）
  const removeCharacterFromRun = useCallback(async (characterId) => {
    if (!currentRunId || !currentUser) return { success: false, error: '未选择 Run' };
    
    setSyncing(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/runs/${currentRunId}/characters/${characterId}?version=${runVersion}&username=${encodeURIComponent(currentUser.username)}`,
        { method: 'DELETE' }
      );
      
      const data = await res.json();
      
      if (res.ok) {
        // 更新本地状态
        setCharacters(prev => prev.filter(c => c.id !== characterId));
        setRunVersion(data.version);
        setRuns(prev => prev.map(r => r.id === currentRunId ? { ...r, characters: (r.characters || []).filter(c => c.id !== characterId), version: data.version } : r));
        setSyncing(false);
        return { success: true };
      } else {
        setSyncing(false);
        if (data.detail?.error === 'VERSION_CONFLICT') {
          await refreshCurrentRun();
          return { success: false, error: data.detail.message, needRefresh: true };
        }
        return { success: false, error: data.detail?.message || data.detail || '删除失败' };
      }
    } catch (e) {
      console.error(e);
      setSyncing(false);
      return { success: false, error: '网络错误' };
    }
  }, [currentRunId, currentUser, runVersion, refreshCurrentRun]);

  // 更新角色信息（使用增量 API）
  const updateCharacterInRun = useCallback(async (characterId, updates) => {
    if (!currentRunId || !currentUser) return { success: false, error: '未选择 Run' };
    
    setSyncing(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/runs/${currentRunId}/characters/${characterId}?username=${encodeURIComponent(currentUser.username)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ character_id: characterId, updates, version: runVersion })
        }
      );
      
      const data = await res.json();
      
      if (res.ok) {
        // 更新本地状态
        setCharacters(prev => prev.map(c => c.id === characterId ? data.character : c));
        setRunVersion(data.version);
        setRuns(prev => prev.map(r => r.id === currentRunId ? { 
          ...r, 
          characters: (r.characters || []).map(c => c.id === characterId ? data.character : c),
          version: data.version 
        } : r));
        setSyncing(false);
        return { success: true, character: data.character };
      } else {
        setSyncing(false);
        if (data.detail?.error === 'VERSION_CONFLICT') {
          await refreshCurrentRun();
          return { success: false, error: data.detail.message, needRefresh: true };
        } else if (data.detail?.error === 'TEAM_FULL') {
          return { success: false, error: data.detail.message, teamFull: true, team: data.detail.team };
        }
        return { success: false, error: data.detail?.message || data.detail || '更新失败' };
      }
    } catch (e) {
      console.error(e);
      setSyncing(false);
      return { success: false, error: '网络错误' };
    }
  }, [currentRunId, currentUser, runVersion, refreshCurrentRun]);

  // 批量更新状态（使用增量 API）
  const batchUpdateStatus = useCallback(async (round, statusMap) => {
    if (!currentRunId || !currentUser) return { success: false, error: '未选择 Run' };
    
    setSyncing(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/runs/${currentRunId}/batch-status?username=${encodeURIComponent(currentUser.username)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status_map: statusMap, round, version: runVersion })
        }
      );
      
      const data = await res.json();
      
      if (res.ok) {
        // 更新本地状态
        const field = round === 1 ? 'round1Status' : 'round2Status';
        setCharacters(prev => prev.map(c => {
          const charId = String(c.id);
          if (statusMap[charId]) {
            return { ...c, [field]: statusMap[charId] };
          }
          return c;
        }));
        setRunVersion(data.version);
        setSyncing(false);
        return { success: true, message: data.message };
      } else {
        setSyncing(false);
        if (data.detail?.error === 'VERSION_CONFLICT') {
          await refreshCurrentRun();
          return { success: false, error: data.detail.message, needRefresh: true };
        }
        return { success: false, error: data.detail?.message || data.detail || '更新失败' };
      }
    } catch (e) {
      console.error(e);
      setSyncing(false);
      return { success: false, error: '网络错误' };
    }
  }, [currentRunId, currentUser, runVersion, refreshCurrentRun]);

  // 更新队伍数量
  const updateTeamCount = useCallback(async (newTeamCount) => {
    if (!currentRunId || !currentUser) return;

    // 乐观更新：先记住旧值，保存失败时回滚，避免与服务端不一致后被轮询"打回"
    let prevTeamCount;
    setTeamCount(prev => { prevTeamCount = prev; return newTeamCount; });

    try {
      const res = await fetch(`${API_BASE_URL}/api/runs/${currentRunId}?username=${encodeURIComponent(currentUser.username)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ team_count: newTeamCount })
      });

      if (res.ok) {
        // 同步到 runs 列表，保持本地一致
        setRuns(prev => prev.map(r => r.id === currentRunId ? { ...r, teamCount: newTeamCount } : r));
      } else {
        setTeamCount(prevTeamCount);  // 回滚
        let msg = '保存队伍数量失败';
        if (res.status === 403) msg = '只有 Host 或管理员可以修改队伍数量';
        messageApi.error(msg);
      }
    } catch (e) {
      console.error(e);
      setTeamCount(prevTeamCount);  // 回滚
      messageApi.error('保存队伍数量失败');
    }
  }, [currentRunId, currentUser, messageApi]);

  const loadHistory = useCallback(async () => {
    try { const res = await fetch(`${API_BASE_URL}/api/history`); if (res.ok) setHistoryList(await res.json()); } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadFromServer(); loadHistory(); }, [loadFromServer, loadHistory]);
  
  // 定时刷新当前 Run 数据（每 30 秒）
  // 仅在当前处于 Boss Run Manager 标签页时才轮询，切到其他 tab 就停止，避免无谓请求
  useEffect(() => {
    if (!currentRunId || isHistoryView || mainTab !== 'bossRun') return;

    const intervalId = setInterval(() => {
      // 页面被切到后台（其他浏览器标签）时跳过这一次刷新
      if (document.visibilityState === 'visible') {
        refreshCurrentRun(true);  // silent refresh
      }
    }, 30000);  // 30 秒

    return () => clearInterval(intervalId);
  }, [currentRunId, isHistoryView, mainTab, refreshCurrentRun]);

  // 创建新 Run 成功后的回调
  const handleCreateRunSuccess = (newRun) => {
    setRuns(prev => [...prev, newRun]);
    setCurrentRunId(newRun.id);
    // 使用新 Run 中已有的角色（包含 Host）
    setCharacters(newRun.characters || []);
    setTeamCount(newRun.teamCount || 2);
    setBossType(newRun.bossType || 'HT');
    setRunVersion(newRun.version || 1);  // 设置版本号
    setCreateRunVisible(false);
    
    // 更新 linkedCharacters（如果有 Host 角色）
    if (newRun.characters?.length > 0 && currentUser) {
      const hostChar = newRun.characters[0];
      setLinkedCharacters(prev => ({
        ...prev,
        [hostChar.name.toLowerCase()]: currentUser.username
      }));
    }
  };

  // 编辑 Run 名称成功后的回调
  const handleEditRunNameSuccess = (newName) => {
    setRuns(prev => prev.map(r => r.id === currentRunId ? { ...r, name: newName } : r));
    setEditRunNameVisible(false);
  };

  // 转移 Host 给另一个角色
  const handleTransferHost = async (targetCharacterId) => {
    if (!currentRunId || !currentUser) {
      messageApi.error('未选择 Run');
      return;
    }
    
    // 找到当前的 Host
    const currentHostChar = characters.find(c => c.roles?.includes('host'));
    if (!currentHostChar) {
      messageApi.error('当前没有 Host');
      return;
    }
    
    // 找到目标角色
    const targetChar = characters.find(c => c.id === targetCharacterId);
    if (!targetChar) {
      messageApi.error('目标角色不存在');
      return;
    }
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/runs/${currentRunId}/transfer-host?username=${encodeURIComponent(currentUser.username)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          target_character_id: targetCharacterId,
          version: runVersion 
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        messageApi.success(`Host 已转移给 ${targetChar.name}`);
        setTransferHostVisible(false);
        // 从服务器刷新数据以确保状态一致
        await refreshCurrentRun();
      } else {
        if (data.detail?.error === 'VERSION_CONFLICT') {
          messageApi.error(data.detail.message);
          await refreshCurrentRun();
        } else {
          messageApi.error(data.detail || '转移 Host 失败');
        }
      }
    } catch (e) {
      console.error(e);
      messageApi.error('转移 Host 失败');
    }
  };

  // 删除当前 Run
  const handleDeleteRun = async () => {
    if (!currentRunId || !currentUser) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/runs/${currentRunId}?username=${encodeURIComponent(currentUser.username)}`, { method: 'DELETE' });
      if (res.ok) {
        messageApi.success('Run 已删除');
        loadFromServer();
      } else {
        const data = await res.json();
        messageApi.error(data.detail || '删除失败');
      }
    } catch (e) {
      messageApi.error('删除失败');
    }
  };

  const handleSave = async (values) => {
    if (editingCharacter) {
      // 编辑现有角色 - 使用增量 API
      const result = await updateCharacterInRun(editingCharacter.id, values);
      if (result.success) {
        messageApi.success('更新成功！');
        setModalVisible(false); 
        setEditingCharacter(null);
      } else {
        if (result.teamFull) {
          messageApi.error(result.error);
        } else if (result.needRefresh) {
          messageApi.warning('数据已更新，请重新操作');
        } else {
          messageApi.error(result.error || '更新失败');
        }
      }
    } else {
      // 添加新角色 - 使用增量 API
      const characterName = values.name.trim();
      
      // 检查角色是否已在当前 Run 中（本地检查，服务端也会检查）
      if (characters.some(c => c.name.toLowerCase() === characterName.toLowerCase())) { 
        messageApi.warning('角色已在当前 Run 中！'); 
        return; 
      }
      
      const newCharacter = { 
        ...values, 
        name: characterName
      };
      
      // 调用增量 API 添加角色
      const result = await addCharacterToRun(newCharacter);
      
      if (result.success) {
        const isFirstCharacter = characters.length === 0;
        if (isFirstCharacter || result.character?.roles?.includes('host')) {
          messageApi.success('添加成功！作为第一个加入的角色，已自动设为 Host');
        } else {
          messageApi.success('添加成功！');
        }
        setModalVisible(false); 
        setEditingCharacter(null);
      } else {
        if (result.teamFull) {
          messageApi.error(result.error);
        } else if (result.needRefresh) {
          messageApi.warning('数据已更新，请重新操作');
        } else {
          messageApi.error(result.error || '添加失败');
        }
      }
    }
  };

  // 单个角色状态变更 - 使用增量 API
  const handleStatusChange = useCallback(async (charId, field, value) => {
    const result = await updateCharacterInRun(charId, { [field]: value });
    if (!result.success) {
      if (result.needRefresh) {
        messageApi.warning('数据已更新，状态已自动刷新');
      } else {
        messageApi.error(result.error || '更新状态失败');
      }
    }
  }, [updateCharacterInRun, messageApi]);

  // 删除角色（Host 不能直接删除，需要先转移）- 使用增量 API
  const handleDeleteCharacter = useCallback(async (charId) => {
    const charToDelete = characters.find(c => c.id === charId);
    if (!charToDelete) return;
    
    // 如果要删除的是 Host
    if (charToDelete.roles?.includes('host')) {
      // 如果只剩一个角色，允许删除（删除后 Run 为空）
      if (characters.length > 1) {
        // 否则不允许删除 Host
        messageApi.error('Host 不能直接删除，请先将 Host 身份转移给其他角色');
        return;
      }
    }
    
    // 使用增量 API 删除
    const result = await removeCharacterFromRun(charId);
    if (result.success) {
      messageApi.success('角色已删除');
    } else {
      if (result.needRefresh) {
        messageApi.warning('数据已更新，请重新操作');
      } else {
        messageApi.error(result.error || '删除失败');
      }
    }
  }, [characters, messageApi, removeCharacterFromRun]);

  // 批量更新状态 - 使用增量 API
  const handleBatchUpdate = async (round, statusMap) => {
    const result = await batchUpdateStatus(round, statusMap);
    if (result.success) {
      messageApi.success(result.message || `Round ${round} 状态已更新`);
      setBatchModal({ visible: false, round: 1 });
    } else {
      if (result.needRefresh) {
        messageApi.warning('数据已更新，请重新操作');
      } else {
        messageApi.error(result.error || '更新失败');
      }
      setBatchModal({ visible: false, round: 1 });
    }
  };

  const handleCompleteRun = async (runNameParam) => {
    if (!runNameParam.trim()) { messageApi.warning('请输入运行名称'); return; }
    if (!currentRunId) { messageApi.warning('请先选择一个 Run'); return; }
    try {
      const url = `${API_BASE_URL}/api/history/complete?run_name=${encodeURIComponent(runNameParam.trim())}&run_id=${currentRunId}&username=${encodeURIComponent(currentUser?.username || '')}`;
      const res = await fetch(url, { method: 'POST' });
      const data = await res.json();
      if (res.ok) { 
        messageApi.success('运行已完成并保存到历史！'); 
        setCompleteModalVisible(false);
        await loadFromServer();
        await loadHistory(); 
      }
      else messageApi.error(data.detail || '操作失败');
    } catch (e) { messageApi.error('操作失败'); }
  };

  const handleLoadHistory = async (runId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/history/${runId}/restore`, { method: 'POST' });
      if (res.ok) { const data = await res.json(); setCharacters(data.characters || []); setTeamCount(data.teamCount || 2); setIsHistoryView(true); setHistoryViewName(data.runName); messageApi.success('已加载历史记录'); }
    } catch (e) { messageApi.error('加载失败'); }
  };

  const handleBackToCurrent = () => { loadFromServer(); setIsHistoryView(false); setHistoryViewName(''); };

  const charactersByTeam = useMemo(() => {
    const groups = {};
    for (let i = 1; i <= teamCount; i++) groups[i] = characters.filter(c => c.team === i).sort((a, b) => (a.isLeader ? -1 : 0) - (b.isLeader ? -1 : 0));
    return groups;
  }, [characters, teamCount]);

  // 检查是否可以完成运行
  const canComplete = useMemo(() => {
    if (characters.length === 0) return false;
    return characters.every(c => {
      if (c.round1 && (!c.round1Status || c.round1Status === 'NotDone')) return false;
      if (c.round2 && (!c.round2Status || c.round2Status === 'NotDone')) return false;
      return true;
    });
  }, [characters]);

  // 当前 Boss 类型的状态选项
  const currentStatusOptions = useMemo(() => {
    return getBossStatusOptions(bossType, bossConfig);
  }, [bossType, bossConfig]);

  const teamTabs = useMemo(() => Array.from({ length: teamCount }, (_, i) => {
    const teamNum = i + 1; const teamChars = charactersByTeam[teamNum] || [];
    return { key: teamNum.toString(), label: <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: TEAM_COLORS[teamNum], marginRight: 6 }} />队伍 {teamNum}<Badge count={teamChars.length} style={{ marginLeft: 8, backgroundColor: teamChars.length >= MAX_TEAM_MEMBERS ? '#ff4d4f' : '#52c41a' }} /></span>,
      children: teamChars.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无成员" /> : <Row gutter={[12, 12]}>{teamChars.map(c => <Col key={c.id} xs={12} sm={8} md={6} lg={5} xl={4}><CharacterCard character={c} onDelete={handleDeleteCharacter} onEdit={c => { setEditingCharacter(c); setModalVisible(true); }} onStatusChange={handleStatusChange} isHistoryView={isHistoryView} statusOptions={currentStatusOptions} currentUser={currentUser} isHost={isCurrentUserHost} isAdmin={isAdmin} linkedCharacters={linkedCharacters} characters={characters} onJobLoaded={(name, job) => setCharacterJobs(prev => ({ ...prev, [name]: job }))} /></Col>)}</Row> };
  }), [charactersByTeam, teamCount, characters, isHistoryView, handleStatusChange, handleDeleteCharacter, currentStatusOptions, currentUser, isCurrentUserHost, isAdmin, linkedCharacters]);

  const allTab = { key: 'all', label: <span><UsergroupAddOutlined style={{ marginRight: 6 }} />全部<Badge count={characters.length} style={{ marginLeft: 8, backgroundColor: '#1890ff' }} /></span>,
    children: characters.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="还没有角色" /> : <div>{Array.from({ length: teamCount }, (_, i) => i + 1).map(n => <TeamSection key={n} teamNum={n} characters={charactersByTeam[n] || []} onDelete={handleDeleteCharacter} onEdit={c => { setEditingCharacter(c); setModalVisible(true); }} onStatusChange={handleStatusChange} isHistoryView={isHistoryView} statusOptions={currentStatusOptions} currentUser={currentUser} isHost={isCurrentUserHost} isAdmin={isAdmin} linkedCharacters={linkedCharacters} allCharacters={characters} onJobLoaded={(name, job) => setCharacterJobs(prev => ({ ...prev, [name]: job }))} />)}</div> };

  // 主要 Tab 配置（必须在条件返回之前，因为 useMemo 不能条件调用）
  const mainTabItems = useMemo(() => {
    // 移动端保留报名相关 + 已适配的轻量页（Bank Counter / Drop Stats / Admin）
    if (isMobile) {
      const mobileItems = [
        { key: 'rules', label: <span><FileTextOutlined /> Rules</span> },
        { key: 'bossRun', label: <span><ThunderboltOutlined /> Boss Run</span> },
        { key: 'bankCounter', label: <span><ExportOutlined /> Bank Counter</span> },
        { key: 'dropStats', label: <span><BarChartOutlined /> Drop Stats</span> },
      ];
      if (isAdmin) {
        mobileItems.push({ key: 'admin', label: <span><CrownOutlined /> Admin</span> });
      }
      return mobileItems;
    }

    const items = [
      {
        key: 'rules',
        label: <span><FileTextOutlined /> Rules</span>,
      },
      {
        key: 'bossRun',
        label: <span><ThunderboltOutlined /> Boss Run Manager</span>,
      },
      {
        key: 'bossRush',
        label: <span><ThunderboltOutlined /> Boss Rush</span>,
      },
      {
        key: 'bonusBank',
        label: <span><BankOutlined /> Bonus Bank</span>,
      },
      {
        key: 'bankCounter',
        label: <span><ExportOutlined /> Bank Counter</span>,
      },
      {
        key: 'dropStats',
        label: <span><BarChartOutlined /> Drop Stats</span>,
      },
    ];

    // 仅管理员可见
    if (isAdmin) {
      items.push({
        key: 'admin',
        label: <span><CrownOutlined /> Admin</span>,
      });
    }

    return items;
  }, [isAdmin, isMobile]);

  const appTheme = {
    algorithm: darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: { colorPrimary: '#ff85a2', borderRadius: 8 },
  };

  // 系统初始化引导（首次使用）
  if (initStatus === null) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin size="large" /></div>;
  }
  if (initStatus === false) {
    return <ConfigProvider theme={appTheme}><InitPage onInitialized={() => setInitStatus(true)} /></ConfigProvider>;
  }

  // 如果未登录，显示登录页面
  if (!currentUser) {
    return <ConfigProvider theme={appTheme}><LoginPage onLogin={handleLogin} /></ConfigProvider>;
  }

  // Boss Run Manager 内容
  const BossRunContent = (
    <>
      {/* Run 选择器 */}
      <div style={{ background: 'var(--surface-card)', borderRadius: 12, padding: isMobile ? '10px 12px' : '12px 16px', marginBottom: 16 }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', flexWrap: 'wrap', gap: isMobile ? 8 : 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 0 }}>
            <Text strong style={{ fontSize: isMobile ? 13 : 14 }}>进行中的 Run ({runs.length}/{maxRuns})</Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {runs.map(run => (
                <Tag
                  key={run.id}
                  color={run.id === currentRunId ? 'blue' : 'default'}
                  style={{ cursor: 'pointer', padding: isMobile ? '6px 12px' : '4px 12px', fontSize: 14, margin: 0, borderRadius: 8, display: 'flex', alignItems: 'center', gap: 4 }}
                  onClick={() => switchRun(run.id)}
                >
                  <BossTypeTag bossType={run.bossType} /> {run.name}
                  {run.characters?.length > 0 && <span>({run.characters.length}人)</span>}
                </Tag>
              ))}
              {runs.length < maxRuns && (
                <Button type="dashed" size={isMobile ? 'middle' : 'small'} icon={<PlusOutlined />} onClick={() => setCreateRunVisible(true)}>
                  新建 Run
                </Button>
              )}
            </div>
          </div>
          {currentRun && !isHistoryView && (isCurrentUserHost || isAdmin) && (
            <Popconfirm title="确定删除此 Run？所有登记数据将丢失！" onConfirm={handleDeleteRun} okText="删除" cancelText="取消">
              <Button size="small" danger icon={<DeleteOutlined />} block={isMobile}>删除当前 Run</Button>
            </Popconfirm>
          )}
        </div>
      </div>

      {/* 当前 Run 信息 */}
      {currentRun && (
        <div style={{ background: 'var(--surface-card)', borderRadius: 12, padding: '10px 16px', marginBottom: 16, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: isMobile ? 8 : 16, flexWrap: 'wrap' }}>
          <BossTypeTag bossType={bossType} />
          <Text strong style={{ fontSize: 16 }}>{currentRun.name}</Text>
          {!isHistoryView && (isCurrentUserHost || isAdmin) && (
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => setEditRunNameVisible(true)}
              title="编辑 Run 名称"
            />
          )}
          <Space><Text style={{ whiteSpace: 'nowrap' }}>队伍:</Text><InputNumber min={1} max={10} value={teamCount} onChange={v => updateTeamCount(v || 1)} style={{ width: 60 }} disabled={isHistoryView || !(isCurrentUserHost || isAdmin)} title={!(isCurrentUserHost || isAdmin) ? '只有 Host 或管理员可以修改队伍数量' : ''} /></Space>
        </div>
      )}

      {/* Control Bar */}
      {currentRun && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          {!isHistoryView && <>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingCharacter(null); setModalVisible(true); }}>添加角色</Button>
            {/* 管理操作（批量更新/完成运行）移动端隐藏，仅桌面 Host/管理员使用 */}
            {!isMobile && ((isCurrentUserHost || isAdmin) ? (
              <Dropdown menu={{ items: [{ key: '1', label: 'Round 1' }, { key: '2', label: 'Round 2' }], onClick: ({ key }) => setBatchModal({ visible: true, round: parseInt(key) }) }}><Button icon={<CheckCircleOutlined />}>批量更新状态 <DownOutlined /></Button></Dropdown>
            ) : (
              <Button icon={<CheckCircleOutlined />} disabled title="只有 Host 或管理员可以批量更新状态">批量更新状态</Button>
            ))}
            {!isMobile && <Button icon={<SaveOutlined />} disabled={!canComplete || !(isCurrentUserHost || isAdmin)} onClick={() => setCompleteModalVisible(true)} type={canComplete && (isCurrentUserHost || isAdmin) ? 'primary' : 'default'} danger={canComplete && (isCurrentUserHost || isAdmin)} title={!(isCurrentUserHost || isAdmin) ? '只有 Host 或管理员可以完成运行' : ''}>完成运行</Button>}
          </>}
          <Button icon={<SyncOutlined spin={syncing} />} onClick={loadFromServer} loading={loading}>刷新</Button>
          {!isMobile && <Dropdown menu={{ items: historyList.length === 0 ? [{ key: 'none', label: '暂无历史记录', disabled: true }] : historyList.map(h => ({ key: h.runId, label: `${h.runName} (${h.characterCount}人) - ${new Date(h.completedAt).toLocaleString()}` })), onClick: ({ key }) => key !== 'none' && handleLoadHistory(key) }}><Button icon={<HistoryOutlined />}>历史记录 <DownOutlined /></Button></Dropdown>}
        </div>
      )}

      {/* 没有 Run 时的提示 */}
      {!currentRun && !isHistoryView && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Empty description="还没有进行中的 Run" />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateRunVisible(true)} style={{ marginTop: 16 }}>
            创建第一个 Run
          </Button>
        </div>
      )}

      {/* Stats（移动端隐藏，报名场景不需要统计分析）*/}
      {!isMobile && currentRun && characters.length > 0 && (
        <div style={{ background: 'var(--surface-card)', borderRadius: 12, padding: '10px 16px', marginBottom: 16, display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap', fontSize: 13, alignItems: 'center' }}>
          <Text>总人数: <Tag color="blue">{characters.length}</Tag></Text>
          <Text>R1: <Tag color="green">{characters.filter(c => c.round1).length}</Tag></Text>
          <Text>R2: <Tag color="blue">{characters.filter(c => c.round2).length}</Tag></Text>
          <Text>R1完成: <Tag color="success">{characters.filter(c => c.round1 && c.round1Status === 'AllDone').length}</Tag></Text>
          <Text>R2完成: <Tag color="success">{characters.filter(c => c.round2 && c.round2Status === 'AllDone').length}</Tag></Text>
          {Object.keys(characterJobs).length > 0 && (
            <>
              <Divider type="vertical" />
              {(() => {
                const classCounts = {};
                characters.forEach(c => {
                  const job = characterJobs[c.name];
                  if (job) {
                    const label = getJobClassLabel(job);
                    if (label) classCounts[label] = (classCounts[label] || 0) + 1;
                  }
                });
                return Object.entries(classCounts).map(([label, count]) => (
                  <Text key={label}>{label}: <Tag color={CLASS_COLORS[label] || '#d9d9d9'}>{count}</Tag></Text>
                ));
              })()}
            </>
          )}
          {(isCurrentUserHost || isAdmin) && (
            <Space size={4}>
              {isCurrentUserHost && <Tag color="gold" icon={<CrownOutlined />}>你是 Host</Tag>}
              {isAdmin && !isCurrentUserHost && <Tag color="purple" icon={<CrownOutlined />}>管理员</Tag>}
              {isAdmin && isCurrentUserHost && <Tag color="purple" style={{ marginLeft: 4 }}>管理员</Tag>}
              {!isHistoryView && characters.length > 1 && (
                <Button 
                  size="small" 
                  type="text"
                  icon={<CrownOutlined />}
                  onClick={() => setTransferHostVisible(true)}
                  style={{ color: '#faad14', fontSize: 12 }}
                >
                  转移
                </Button>
              )}
            </Space>
          )}
        </div>
      )}

      {(currentRun || isHistoryView) && (
        <div style={{ background: 'var(--surface-card)', borderRadius: 16, padding: 16 }}>
          <Tabs defaultActiveKey="all" items={[allTab, ...teamTabs]} />
        </div>
      )}
    </>
  );

  return (
    <ConfigProvider theme={appTheme}>
      {contextHolder}
      <Layout style={{ minHeight: '100vh', background: darkMode ? 'linear-gradient(180deg, #17171f 0%, #231a28 50%, #142429 100%)' : 'linear-gradient(180deg, #ffecd2 0%, #fcb69f 50%, #a8edea 100%)' }}>
        <Header style={{ background: 'transparent', height: 'auto', padding: isMobile ? '12px 8px 6px' : '20px 20px 10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, position: isMobile ? 'relative' : 'static' }}>
            {!isMobile && <div style={{ width: 200 }} />}
            <Title level={isMobile ? 4 : 3} style={{ margin: 0, color: '#ff6b95', textAlign: 'center', flex: 1 }}>🍁 MapleLegends</Title>
            <div style={{ width: isMobile ? 'auto' : 200, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, position: isMobile ? 'absolute' : 'static', right: 0, top: '50%', transform: isMobile ? 'translateY(-50%)' : 'none' }}>
              <Button
                type="text"
                icon={themeMode === 'dark' ? <MoonOutlined /> : themeMode === 'system' ? <DesktopOutlined /> : <SunOutlined />}
                onClick={() => setThemeMode(m => (m === 'light' ? 'dark' : m === 'dark' ? 'system' : 'light'))}
                title={themeMode === 'light' ? '白天模式 · 点击切换夜间' : themeMode === 'dark' ? '夜间模式 · 点击切换跟随系统' : '跟随系统 · 点击切换白天'}
              />
              <Button
                type="text"
                icon={<Avatar size="small" style={{ backgroundColor: '#ff85a2' }}>{currentUser?.username?.charAt(0)?.toUpperCase()}</Avatar>}
                onClick={() => setUserProfileVisible(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 4 }}
              >
                {!isMobile && currentUser?.username}
              </Button>
              <Button type="text" icon={<LogoutOutlined />} onClick={handleLogout} danger>{!isMobile && '退出'}</Button>
            </div>
          </div>

          {/* 主导航 Tab */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
            <Tabs
              activeKey={mainTab}
              onChange={setMainTab}
              items={mainTabItems}
              size={isMobile ? 'small' : 'large'}
              style={{ marginBottom: 0, maxWidth: '100%' }}
            />
          </div>
          
          {isHistoryView && mainTab === 'bossRun' && (
            <Alert message={`正在查看历史记录: ${historyViewName}`} type="info" showIcon style={{ marginTop: 10 }} action={<Button size="small" onClick={handleBackToCurrent}>返回当前</Button>} />
          )}
        </Header>
        
        <Content style={{ padding: isMobile ? '8px 8px' : '10px 20px', maxWidth: 1400, margin: '0 auto', width: '100%' }} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          {mainTab === 'bossRun' && BossRunContent}
          {mainTab === 'bossRush' && <BossRush currentUser={currentUser} />}
          {mainTab === 'bonusBank' && <BonusBank currentUser={currentUser} />}
          {mainTab === 'bankCounter' && <BankCounter key={`bc-${refreshKey}`} currentUser={currentUser} />}
          {mainTab === 'dropStats' && <DropStats key={`ds-${refreshKey}`} />}
          {mainTab === 'rules' && <Rules />}
          {mainTab === 'admin' && isAdmin && <AdminPage key={`ad-${refreshKey}`} currentUser={currentUser} onConfigChange={async () => {
            const newConfig = await loadBossConfig();
            if (newConfig) setBossConfig(newConfig);
          }} />}
        </Content>
        <Footer style={{ textAlign: 'center', background: 'transparent', padding: '12px 16px' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            🍁 MapleLegends{version ? ` · v${version}` : ''}
          </Text>
        </Footer>
      </Layout>

      <CharacterModal visible={modalVisible} onCancel={() => { setModalVisible(false); setEditingCharacter(null); }} onSave={handleSave} editingCharacter={editingCharacter} characters={characters} teamCount={teamCount} bossType={bossType} isCurrentUserHost={isCurrentUserHost} currentUser={currentUser} linkedCharacters={linkedCharacters} />
      <BatchUpdateModal visible={batchModal.visible} round={batchModal.round} characters={characters} bossType={bossType} bossConfig={bossConfig} onCancel={() => setBatchModal({ visible: false, round: 1 })} onConfirm={handleBatchUpdate} />
      
      <CompleteRunModal visible={completeModalVisible} onCancel={() => setCompleteModalVisible(false)} onConfirm={handleCompleteRun} bossType={bossType} />

      <UserProfileModal
        visible={userProfileVisible} 
        onClose={() => setUserProfileVisible(false)} 
        user={currentUser} 
        onUserUpdate={setCurrentUser}
      />
      
      <CreateRunModal
        visible={createRunVisible}
        onCancel={() => setCreateRunVisible(false)}
        onSuccess={handleCreateRunSuccess}
        existingRuns={runs.length}
        maxRuns={maxRuns}
        currentUser={currentUser}
      />
      
      <EditRunNameModal
        visible={editRunNameVisible}
        onCancel={() => setEditRunNameVisible(false)}
        onSuccess={handleEditRunNameSuccess}
        currentRun={currentRun}
        currentUser={currentUser}
      />
      
      {/* 转移 Host 弹窗 */}
      <Modal
        title={<><CrownOutlined style={{ color: '#faad14' }} /> 转移 Host</>}
        open={transferHostVisible}
        onCancel={() => setTransferHostVisible(false)}
        footer={null}
        width={400}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">选择要将 Host 身份转移给的角色：</Text>
        </div>
        <div style={{ maxHeight: 300, overflow: 'auto' }}>
          {characters
            .filter(c => !c.roles?.includes('host'))
            .map(c => (
              <div 
                key={c.id}
                style={{ 
                  padding: '12px 16px', 
                  border: '1px solid #f0f0f0', 
                  borderRadius: 8, 
                  marginBottom: 8,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#ff85a2'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#f0f0f0'}
              >
                <div>
                  <Text strong>{c.name}</Text>
                  <div style={{ fontSize: 12, color: '#999' }}>
                    队伍 {c.team} · {(c.roles || []).map(r => ROLE_OPTIONS.find(ro => ro.value === r)?.label || r).join(', ')}
                  </div>
                </div>
                <Button 
                  type="primary" 
                  size="small"
                  onClick={() => handleTransferHost(c.id)}
                >
                  转移
                </Button>
              </div>
            ))}
        </div>
        {characters.filter(c => !c.roles?.includes('host')).length === 0 && (
          <Empty description="没有可转移的角色" />
        )}
      </Modal>
    </ConfigProvider>
  );
}

export default App;